<template>
  <Teleport to="body">
    <transition name="cliente-side-drawer">
      <div
        v-if="open"
        class="cliente-side-drawer-backdrop"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || title"
        @click.self="onClose"
      >
        <aside class="cliente-side-drawer-panel" :class="[{ 'cliente-side-drawer-panel--wide': wide }, panelClass]">
          <header v-if="showHeader" class="cliente-side-drawer-header">
            <div class="cliente-side-drawer-header__title">
              <div
                v-if="$slots.icon"
                class="cliente-side-drawer-header__icon"
                aria-hidden="true"
              >
                <slot name="icon" />
              </div>
              <div class="min-w-0">
                <h2 class="cliente-side-drawer-header__name">{{ title }}</h2>
                <p v-if="subtitle" class="cliente-side-drawer-header__sub">{{ subtitle }}</p>
              </div>
            </div>
            <button type="button" class="cliente-side-drawer-close" :aria-label="t('common.close')" @click="onClose">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <button
            v-else
            type="button"
            class="cliente-side-drawer-close cliente-side-drawer-close--floating"
            :aria-label="t('common.close')"
            @click="onClose"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div class="cliente-side-drawer-body" :class="bodyClass">
            <slot />
          </div>
        </aside>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  wide: { type: Boolean, default: false },
  bodyClass: { type: String, default: '' },
  /** Histórico/WhatsApp já têm cabeçalho próprio no painel */
  showHeader: { type: Boolean, default: true },
  panelClass: { type: String, default: '' }
})

const emit = defineEmits(['close'])

const { t } = useI18n()

const onClose = () => emit('close')
</script>

<style scoped>
.cliente-side-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(3px);
}

.cliente-side-drawer-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(440px, 100vw);
  height: 100%;
  background: var(--bg-primary);
  border-left: 1px solid var(--border-primary);
  box-shadow: -16px 0 48px rgba(0, 0, 0, 0.2);
}

.cliente-side-drawer-panel--wide {
  width: min(520px, 100vw);
}

.cliente-side-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.cliente-side-drawer-header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.cliente-side-drawer-header__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  color: #fff;
}

.cliente-side-drawer-header__icon :deep(svg) {
  width: 22px;
  height: 22px;
}

.cliente-side-drawer-header__name {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cliente-side-drawer-header__sub {
  margin: 2px 0 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
}

.cliente-side-drawer-close {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
}

.cliente-side-drawer-close:hover {
  background: var(--bg-hover, rgba(0, 0, 0, 0.06));
  color: var(--text-primary);
}

.cliente-side-drawer-close svg {
  width: 22px;
  height: 22px;
}

.cliente-side-drawer-close--floating {
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  z-index: 20;
  background: rgba(15, 23, 42, 0.35);
  color: #fff;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.cliente-side-drawer-close--floating:hover {
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
}

.cliente-side-drawer-body {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.cliente-side-drawer-body--flush {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.cliente-side-drawer-enter-active,
.cliente-side-drawer-leave-active {
  transition: opacity 0.22s ease;
}

.cliente-side-drawer-enter-active .cliente-side-drawer-panel,
.cliente-side-drawer-leave-active .cliente-side-drawer-panel {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}

.cliente-side-drawer-enter-from,
.cliente-side-drawer-leave-to {
  opacity: 0;
}

.cliente-side-drawer-enter-from .cliente-side-drawer-panel,
.cliente-side-drawer-leave-to .cliente-side-drawer-panel {
  transform: translateX(100%);
}
</style>
